import { useState, useEffect, useMemo } from "react";
import { useSponsorships } from "@/api/sponsorships";
import { usePayments, usePaymentStats } from "@/api/payments/hooks";
import { useChapters, useAllChaptersStats } from "@/api/chapters";
import {
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Play,
  Pause,
  Maximize2,
  Minimize2,
  CreditCard,
  Building2,
  User,
  Crown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { exportAllChapterStats } from "@/lib/exportChapterStats";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/* ━━━ DESIGN SYSTEM (APPLE THEME) ━━━ */
const THEME = {
  colors: {
    bg: "bg-[#F5F5F7]", // Apple Light Gray
    card: "bg-[#FFFFFF]", // Pure White
    accent: "bg-[#0071E3]", // System Blue
    accentText: "text-[#0071E3]",
    dark: "bg-[#1D1D1F]", // Apple Dark Gray
    textMain: "text-[#1D1D1F]",
    textMuted: "text-[#86868B]", // System Gray
    border: "border-[#D2D2D7]", // System Gray 4
  },
  fonts: {
    heading: "font-sans font-semibold tracking-tight",
    label: "font-sans text-[11px] font-medium uppercase tracking-wider",
  },
  card: "rounded-[20px] shadow-sm border border-[#D2D2D7]",
};

const SLIDE_DURATION = 15000;
// Static fallback rates (Real-time hook below)
const STATIC_RATES = {
  INR: 1,
  AED: 23,
  SAR: 22.5,
  QAR: 23.2,
  KWD: 275,
  BHD: 224,
  OMR: 219,
  USD: 83.5,
  EUR: 90,
  GBP: 105,
};

/* ━━━ REAL-TIME CURRENCY HOOK ━━━ */
const useCurrencyRates = () => {
  const [rates, setRates] = useState(STATIC_RATES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await fetch("https://open.er-api.com/v6/latest/INR");
        const data = await res.json();
        if (data && data.rates) {
          const newRates = { INR: 1 };
          Object.keys(STATIC_RATES).forEach((key) => {
            if (key !== "INR" && data.rates[key]) {
              newRates[key] = 1 / data.rates[key];
            } else if (key !== "INR") {
              newRates[key] = STATIC_RATES[key];
            }
          });
          setRates(newRates);
        }
      } catch (err) {
        console.error("Failed to fetch rates, using fallback", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRates();
    const interval = setInterval(fetchRates, 3600000);
    return () => clearInterval(interval);
  }, []);

  return { rates, loading };
};

/* ━━━ UTILS ━━━ */
const fmtMoney = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

const fmtCompact = (n) => {
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n)}`;
};

const pctChange = (current, previous) => {
  if (previous === 0 && current === 0) return 0;
  if (previous === 0) return 100;
  return Math.round(((current - previous) / previous) * 100);
};

/* ━━━ ANIMATION COMPONENTS ━━━ */
const ScrambleText = ({ value, className, duration = 1000 }) => {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const chars = "0123456789$#%&?";
    let start = 0;
    const steps = 20;
    const interval = setInterval(() => {
      start++;
      if (start > steps) {
        setDisplay(value);
        clearInterval(interval);
      } else {
        setDisplay(
          String(value)
            .split("")
            .map((char) =>
              /[^0-9]/.test(char) || Math.random() < start / steps
                ? char
                : chars[Math.floor(Math.random() * chars.length)],
            )
            .join(""),
        );
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [value, duration]);
  return <span className={className}>{display}</span>;
};

const CountUp = ({ value, className, formatter }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const end = parseFloat(String(value).replace(/,/g, "")) || 0;
    let start = 0;
    if (start === end) return;

    const duration = 1500;
    const startTime = performance.now();

    // Use a ref or state to keep track if we wanted smooth transitions between updates,
    // but for now simple 0->value animation is fine.

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      setCount(start + (end - start) * ease);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]);

  const displayValue = formatter
    ? formatter(count)
    : Math.round(count).toLocaleString();

  return <span className={className}>{displayValue}</span>;
};

/* ━━━ UI BLOCK COMPONENT ━━━ */
const HatchPattern = () => (
  <svg height="0" width="0" className="absolute">
    <defs>
      <pattern
        id="hatchDark"
        width="6"
        height="6"
        patternTransform="rotate(45 0 0)"
        patternUnits="userSpaceOnUse"
      >
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="6"
          style={{ stroke: "#000", strokeWidth: 1.5 }}
          opacity="0.05"
        />
      </pattern>
      {/* Updated to International Orange */}
      <pattern
        id="hatchAccent"
        width="6"
        height="6"
        patternTransform="rotate(45 0 0)"
        patternUnits="userSpaceOnUse"
      >
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="6"
          style={{ stroke: "#0071E3", strokeWidth: 1.5 }}
          opacity="0.3"
        />
      </pattern>
    </defs>
  </svg>
);

const BentoCard = ({
  children,
  className,
  style, // Accept style prop
  dark = false,
  accent = false,
  delay = 0,
}) => {
  let bgClass = THEME.colors.card;
  let textClass = THEME.colors.textMain;

  if (dark) {
    bgClass = THEME.colors.dark;
    textClass = "text-white";
  } else if (accent) {
    bgClass = THEME.colors.accent;
    textClass = "text-white";
  }

  // If an inline backgroundColor is provided via `style`, prefer it and make
  // the Tailwind background class transparent so there is no visual conflict.
  if (style && style.backgroundColor) {
    bgClass = "bg-transparent";
  }

  return (
    <div
      className={cn(
        bgClass,
        textClass,
        THEME.card,
        "p-6 relative overflow-hidden flex flex-col justify-between transition-all duration-500 hover:shadow-xl hover:shadow-blue-500/10",
        className,
      )}
      style={{
        animationDelay: `${delay}ms`,
        animationFillMode: "forwards",
        opacity: 0,
        animation: "fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        ...style, // Spread custom styles (like backgroundColor)
      }}
    >
      {children}
    </div>
  );
};

const Label = ({ children, className, darkBg = false }) => (
  <p
    className={cn(
      THEME.fonts.label,
      darkBg ? "text-white/60" : "text-black/40",
      "mb-1",
      className,
    )}
  >
    {children}
  </p>
);

/* ══════════════════════════════════════════════════════════════════════
   SLIDE 1: PAYMENTS (Summary & Trends)
   ══════════════════════════════════════════════════════════════════════ */

const MetricCard = ({
  title,
  amount,
  count,
  prevAmount,
  icon: Icon,
  delay,
  color = "#0071E3",
  dark = false,
}) => {
  const change = pctChange(amount, prevAmount);
  const isPositive = change >= 0;

  // Solid background style with Apple colors
  // Overriding dark/light default classes when color is provided to create solid vibrant cards
  const isSolid = true; // User requested solid bg

  return (
    <BentoCard
      duration={0}
      delay={delay}
      className={cn(
        "relative overflow-hidden flex flex-col justify-between p-6 group cursor-default border-0 shadow-lg !text-white",
        // when a color is supplied we rely on the inline style; keep underlying bg transparent
        color ? "bg-transparent" : dark ? "bg-[#1D1D1F]" : "bg-white",
      )}
      style={{
        backgroundColor: color, // Solid background
        boxShadow: `0 10px 30px -10px ${color}80`, // Colored shadow
      }}
    >
      <div className="flex justify-between items-start z-10">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm transition-transform duration-500 group-hover:scale-110">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <Label className="!mb-0 text-sm !text-white/90 font-semibold tracking-wide">
            {title}
          </Label>
        </div>
        {prevAmount !== undefined && (
          <div
            className={cn(
              "flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-white/20 backdrop-blur-md text-white border border-white/10",
            )}
          >
            {isPositive ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {Math.abs(change)}%
          </div>
        )}
      </div>

      <div className="mt-6 z-10 flex flex-col items-start text-white">
        <h3 className="text-3xl font-bold tracking-tight transition-all duration-300 group-hover:translate-x-1">
          <CountUp
            value={amount}
            className=""
            formatter={(v) => fmtCompact(v)}
          />
        </h3>
        <p className="text-xs font-medium mt-1 uppercase tracking-wide opacity-80 text-white/80">
          <CountUp value={count} /> Transactions
        </p>
      </div>

      {/* Decor - Subtle gradient overlay instead of blob for solid cards to maintain readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />
      <div className="absolute -bottom-10 -right-10 h-40 w-40 bg-white/10 rounded-full blur-3xl opacity-50 pointer-events-none transition-transform duration-700 group-hover:scale-150" />
    </BentoCard>
  );
};

const PaymentsSlide = ({ stats }) => {
  const chartData = stats.last7Days || [];

  // Apple Colors Palette
  const COLORS = {
    blue: "#0071E3",
    purple: "#AF52DE",
    pink: "#FF2D55",
    orange: "#FF9500",
    green: "#34C759",
    teal: "#5AC8FA",
    indigo: "#5856D6",
  };

  return (
    <div className="h-full p-8 grid grid-cols-12 grid-rows-12 gap-6 bg-[#F5F5F7]">
      {/* ── METRIC CARDS ROW ── */}
      <div className="col-span-12 row-span-4 grid grid-cols-4 gap-6">
        <MetricCard
          title="Today"
          icon={Zap}
          amount={stats.todayAmountINR}
          count={stats.todayCount}
          prevAmount={stats.yesterdayAmountINR} // Compare to yesterday
          delay={0}
          color={COLORS.blue}
        />
        <MetricCard
          title="Yesterday"
          icon={RefreshCw}
          amount={stats.yesterdayAmountINR}
          count={stats.yesterdayCount}
          prevAmount={stats.dayBeforeYesterdayAmountINR}
          delay={100}
          color={COLORS.purple}
        />
        <MetricCard
          title="This Month"
          icon={CreditCard}
          amount={stats.thisMonthAmountINR}
          count={stats.thisMonthCount}
          // Approximation for previous month could be complex, omitting comparison for simplicity or using placeholder
          delay={200}
          dark // distinct style
          color={COLORS.orange}
        />
        <MetricCard
          title="Total Revenue"
          icon={Crown}
          amount={stats.totalCollectedINR}
          count={stats.totalPayments}
          delay={300}
          color={COLORS.green}
        />
      </div>

      {/* ── LOWER SECTION ── */}

      {/* CHART */}
      <BentoCard
        className="col-span-8 row-span-8 bg-white border border-[#E5E5EA] !p-0 flex flex-col"
        delay={400}
      >
        <div className="p-6 border-b border-[#E5E5EA] flex justify-between items-center">
          <div>
            <Label>Revenue Trend</Label>
            <h4 className="text-xl font-bold text-[#1D1D1F]">Last 7 Days</h4>
          </div>
          <div className="flex gap-2">
            <div className="flex items-center gap-2 text-xs font-medium text-[#86868B]">
              <div
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: COLORS.purple }}
              />{" "}
              Performance
            </div>
          </div>
        </div>
        <div className="flex-1 w-full pb-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="gradientGraph" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={COLORS.purple}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={COLORS.purple}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#86868B", fontSize: 10, fontWeight: 600 }}
                dy={10}
              />
              <Tooltip
                cursor={{
                  stroke: COLORS.purple,
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-[#1D1D1F] text-white text-xs px-3 py-2 rounded-lg shadow-xl font-bold">
                        {fmtMoney(payload[0].value)}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="val"
                stroke={COLORS.purple}
                strokeWidth={3}
                fill="url(#gradientGraph)"
                isAnimationActive={true}
                animationDuration={1500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </BentoCard>

      {/* SIDEBAR STATS */}
      <div className="col-span-4 row-span-8 flex flex-col gap-6">
        <BentoCard
          className="flex-1 bg-white border border-[#E5E5EA] relative overflow-hidden"
          delay={500}
        >
          <div className="z-10 relative h-full flex flex-col">
            <Label>Campaign Goal</Label>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-4xl font-bold text-[#1D1D1F]">
                {stats.globalCompletionPct}%
              </span>
              <span className="text-sm font-medium text-[#86868B]">
                Achieved
              </span>
            </div>
            <div className="w-full bg-[#F5F5F7] h-2 rounded-full mt-4 overflow-hidden">
              <div
                className="h-full bg-[#0071E3] rounded-full transition-all duration-1000"
                style={{
                  width: `${Math.min(stats.globalCompletionPct, 100)}%`,
                }}
              />
            </div>
            <div className="mt-auto pt-6">
              <div className="flex justify-between items-center text-sm border-t border-[#E5E5EA] pt-4">
                <span className="text-[#86868B] font-medium">Target</span>
                <span className="font-bold text-[#1D1D1F]">
                  {fmtCompact(stats.totalTargetINR)}
                </span>
              </div>
            </div>
          </div>
          {/* Background pattern */}
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <Zap className="h-32 w-32 text-[#0071E3] -rotate-12" />
          </div>
        </BentoCard>

        <BentoCard
          className="h-1/3 bg-[#1D1D1F] text-white flex flex-col justify-center items-center text-center p-6 relative overflow-hidden"
          delay={600}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-transparent pointer-events-none" />
          <Crown className="h-8 w-8 text-[#FFD700] mb-3 z-10" />
          <div className="text-sm font-medium opacity-60 uppercase tracking-widest z-10">
            Top Day
          </div>
          <div className="text-2xl font-bold mt-1 z-10">
            {fmtCompact(stats.topDayStats?.amount || 0)}
          </div>
          <div className="text-xs opacity-50 mt-1 z-10">
            {stats.topDayStats?.date || "-"}
          </div>
        </BentoCard>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   SLIDE 2: SPONSORSHIPS
   ══════════════════════════════════════════════════════════════════════ */
const SponsorshipsSlide = ({ stats }) => {
  const { mahallu, individual } = stats.sponsorshipCategories;
  const totalPromised = mahallu.promisedINR + individual.promisedINR;
  const totalCollected = mahallu.paidINR + individual.paidINR;
  const totalPending = totalPromised - totalCollected;

  return (
    <div className="h-full p-8 grid grid-cols-12 grid-rows-12 gap-6">
      <BentoCard
        className="col-span-12 row-span-3 flex-row items-center !justify-between px-10"
        delay={0}
      >
        <div className="flex items-center gap-8">
          <div className="h-20 w-20 bg-[#18181B] text-white flex items-center justify-center rounded-[20px] shadow-2xl">
            <CreditCard className="h-10 w-10" />
          </div>
          <div>
            <Label>Total Sponsorships</Label>
            <div className="text-[56px] font-bold tracking-tight leading-none text-[#18181B]">
              <CountUp value={stats.totalSponsorships} />
            </div>
          </div>
        </div>
        <div className="flex gap-16">
          <div className="text-right">
            <Label>Total Promised</Label>
            <div className="text-3xl font-bold text-[#18181B]/40">
              <ScrambleText value={fmtCompact(totalPromised)} />
            </div>
          </div>
          <div className="text-right">
            <Label>Total Collected</Label>
            <div className="text-3xl font-bold text-[#18181B]">
              <ScrambleText value={fmtCompact(totalCollected)} />
            </div>
          </div>
          <div className="text-right">
            <Label>Pending</Label>
            <div className="text-3xl font-bold text-[#0071E3]">
              <ScrambleText value={fmtCompact(totalPending)} />
            </div>
          </div>
        </div>
      </BentoCard>

      <BentoCard dark className="col-span-6 row-span-9 shadow-2xl" delay={150}>
        <div className="flex justify-between items-start">
          <div className="bg-white/10 px-4 py-1.5 rounded-full backdrop-blur-md border border-white/5"></div>
          <Building2 className="text-[#0071E3] h-10 w-10 opacity-90" />
        </div>
        <div className="mt-8">
          <h3 className="text-[56px] font-bold text-white tracking-tight leading-none">
            Mahallu
          </h3>
          <div className="flex gap-3 mt-4">
            <span className="px-3 py-1 rounded bg-white/10 text-xs font-bold text-white/80 uppercase tracking-wider">
              <CountUp value={mahallu.active} /> Active
            </span>
            <span className="px-3 py-1 rounded bg-[#0071E3]/20 text-xs font-bold text-[#0071E3] uppercase tracking-wider">
              <CountUp value={mahallu.pending} /> Pending
            </span>
          </div>
        </div>
        <div className="mt-auto pt-8 grid grid-cols-2 gap-4">
          <div className="bg-[#27272A] p-5 rounded-[16px] border border-white/5">
            <Label darkBg>Promised</Label>
            <div className="text-3xl font-bold text-white/50 mt-1">
              <ScrambleText value={fmtCompact(mahallu.promisedINR)} />
            </div>
          </div>
          <div className="bg-[#27272A] p-5 rounded-[16px] border border-white/5">
            <Label darkBg>Collected</Label>
            <div className="text-3xl font-bold text-white mt-1">
              <ScrambleText value={fmtCompact(mahallu.paidINR)} />
            </div>
          </div>
          <div className="bg-[#27272A] p-5 rounded-[16px] border border-white/5">
            <Label darkBg>Pending</Label>
            <div className="text-3xl font-bold text-[#0071E3] mt-1">
              <ScrambleText
                value={fmtCompact(mahallu.promisedINR - mahallu.paidINR)}
              />
            </div>
          </div>
          <div className="bg-[#0071E3] p-5 rounded-[16px] text-white shadow-lg shadow-blue-500/20 flex flex-col justify-center">
            <Label darkBg>Conversion</Label>
            <div className="text-4xl font-bold mt-1">
              <CountUp value={mahallu.conversionPct} />%
            </div>
          </div>
        </div>
      </BentoCard>

      <BentoCard
        accent
        className="col-span-6 row-span-9 shadow-blue-500/20"
        delay={300}
      >
        <div className="flex justify-between items-start">
          <div className="bg-white/20 px-4 py-1.5 rounded-full backdrop-blur-md border border-white/10"></div>
          <User className="text-white h-10 w-10 opacity-90" />
        </div>
        <div className="mt-8">
          <h3 className="text-[56px] font-bold text-white tracking-tight leading-none">
            Individual
          </h3>
          <div className="flex gap-3 mt-4">
            <span className="px-3 py-1 rounded bg-black/20 text-xs font-bold text-white/90 uppercase tracking-wider">
              <CountUp value={individual.active} /> Active
            </span>
            <span className="px-3 py-1 rounded bg-white/20 text-xs font-bold text-white uppercase tracking-wider">
              <CountUp value={individual.pending} /> Pending
            </span>
          </div>
        </div>
        <div className="mt-auto pt-8 grid grid-cols-2 gap-4">
          <div className="bg-white/20 p-5 rounded-[16px] backdrop-blur-sm border border-white/20">
            <Label darkBg>Promised</Label>
            <div className="text-3xl font-bold text-white/70 mt-1">
              <ScrambleText value={fmtCompact(individual.promisedINR)} />
            </div>
          </div>
          <div className="bg-white/20 p-5 rounded-[16px] backdrop-blur-sm border border-white/20">
            <Label darkBg>Collected</Label>
            <div className="text-3xl font-bold text-white mt-1">
              <ScrambleText value={fmtCompact(individual.paidINR)} />
            </div>
          </div>
          <div className="bg-white/20 p-5 rounded-[16px] backdrop-blur-sm border border-white/20">
            <Label darkBg>Pending</Label>
            <div className="text-3xl font-bold text-white/90 mt-1">
              <ScrambleText
                value={fmtCompact(individual.promisedINR - individual.paidINR)}
              />
            </div>
          </div>
          <div className="bg-white p-5 rounded-[16px] shadow-lg flex flex-col justify-center">
            <Label>Conversion</Label>
            <div className="text-4xl font-bold text-[#0071E3] mt-1">
              <CountUp value={individual.conversionPct} />%
            </div>
          </div>
        </div>

        {/* Background Decoration */}
        <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
      </BentoCard>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   SLIDE 3: LEADERBOARD (Premium Grid)
   ══════════════════════════════════════════════════════════════════════ */
const ChapterLeaderboardSlide = ({ stats }) => {
  const [page, setPage] = useState(0);
  const [isPaused, setIsPaused] = useState(false); // Add pause state
  const ITEMS_PER_PAGE = 15;

  const allChapters = useMemo(() => {
    return stats.chapterLeaderboard.map((ch) => ({
      name: ch.name.length > 12 ? ch.name.substring(0, 10) + ".." : ch.name,
      fullName: ch.name,
      pct: ch.completionPct,
      amt: ch.collectedINR,
      status:
        ch.completionPct >= 75
          ? "high"
          : ch.completionPct >= 40
            ? "mid"
            : "low",
    }));
  }, [stats.chapterLeaderboard]);

  const totalPages = Math.ceil(allChapters.length / ITEMS_PER_PAGE);
  const currentData = allChapters.slice(
    page * ITEMS_PER_PAGE,
    (page + 1) * ITEMS_PER_PAGE,
  );
  const topPerformer = stats.chapterLeaderboard[0];
  const totalTarget = stats.chapterLeaderboard.reduce(
    (acc, curr) => acc + curr.targetINR,
    0,
  );
  const totalCollected = stats.chapterLeaderboard.reduce(
    (acc, curr) => acc + curr.collectedINR,
    0,
  );
  const globalPct =
    totalTarget > 0 ? Math.round((totalCollected / totalTarget) * 100) : 0;

  useEffect(() => {
    if (totalPages <= 1 || isPaused) return; // Respect pause
    const interval = setInterval(() => {
      setPage((prev) => (prev + 1) % totalPages);
    }, 8000);
    return () => clearInterval(interval);
  }, [totalPages, isPaused]);

  const nextPage = () => setPage((p) => (p + 1) % totalPages);
  const prevPage = () => setPage((p) => (p - 1 + totalPages) % totalPages);

  const getBarColor = (status) => {
    // Apple Colors for Status
    if (status === "high") return "#34C759"; // Green (Success)
    if (status === "mid") return "#FF9500"; // Orange
    if (status === "low") return "#FF2D55"; // Pink/Red
    return "#8E8E93";
  };

  return (
    <div className="h-full p-8 grid grid-cols-12 gap-6">
      <BentoCard
        dark
        className="col-span-3 h-full shadow-2xl relative flex flex-col"
        delay={0}
      >
        <div className="mb-8">
          <Label darkBg>Campaign Progress</Label>
          <div className="mt-2 flex items-baseline gap-2">
            <h2 className="text-5xl font-bold text-white tracking-tight">
              {globalPct}%
            </h2>
            <span className="text-sm text-white/50 font-bold">Overall</span>
          </div>
          <div className="w-full h-1.5 bg-white/10 rounded-full mt-4 overflow-hidden">
            <div
              className="h-full bg-[#0071E3]"
              style={{ width: `${globalPct}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between text-[10px] font-bold uppercase text-white/40">
            <span>{fmtCompact(totalCollected)}</span>
            <span>{fmtCompact(totalTarget)}</span>
          </div>
        </div>
        <div className="w-full h-[1px] bg-white/10 mb-8" />
        <div className="flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 bg-[#0071E3] rounded-md text-white shadow-lg shadow-blue-500/20">
              <Crown className="h-4 w-4 fill-current" />
            </div>
            <Label darkBg className="!mb-0 !text-white">
              Current Leader
            </Label>
          </div>
          <h3 className="text-2xl font-bold text-white leading-tight mb-2">
            {topPerformer?.name}
          </h3>
          <div className="text-[#0071E3] text-4xl font-bold tracking-tight mb-1">
            <CountUp value={topPerformer?.completionPct} />%
          </div>
          <div className="mt-auto bg-white/5 rounded-xl p-4 border border-white/5">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold uppercase text-white/40">
                Raised
              </span>
              <span className="text-lg font-bold text-white">
                {fmtCompact(topPerformer?.collectedINR)}
              </span>
            </div>
          </div>
        </div>
      </BentoCard>

      <BentoCard
        className="col-span-9 h-full !p-0 bg-white shadow-sm flex flex-col"
        delay={200}
      >
        <div className="px-8 py-6 border-b border-[#F4F4F5] flex justify-between items-center bg-white rounded-t-[20px]">
          <div>
            <Label>Performance Matrix</Label>
            <div className="text-2xl font-bold text-[#1D1D1F] mt-1 flex items-center gap-3">
              Chapter Rankings
              <span className="px-2 py-0.5 rounded-md bg-[#EFF6FF] text-xs font-bold text-[#0071E3]">
                Page {page + 1} of {totalPages}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#34C759]" />
              <span className="text-[10px] font-bold uppercase text-black/50">
                On Track
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#FF9500]" />
              <span className="text-[10px] font-bold uppercase text-black/50">
                Mid
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#FF2D55]" />
              <span className="text-[10px] font-bold uppercase text-black/50">
                Focus
              </span>
            </div>
          </div>
          <div className="flex gap-2 ml-6 pl-6 border-l border-[#F4F4F5]">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsPaused(!isPaused)}
              className="hover:bg-[#F4F4F5] h-8 w-8"
            >
              {isPaused ? (
                <Play className="h-4 w-4 text-[#86868B]" />
              ) : (
                <Pause className="h-4 w-4 text-[#86868B]" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={prevPage}
              className="hover:bg-[#F4F4F5] h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4 text-black/60" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextPage}
              className="hover:bg-[#F4F4F5] h-8 w-8"
            >
              <ChevronRight className="h-4 w-4 text-black/60" />
            </Button>
          </div>
        </div>
        <div className="flex-1 w-full px-8 pt-8 pb-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={currentData}
              barSize={40}
              margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
            >
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 10,
                  fontWeight: 700,
                  fill: "#71717A",
                  textTransform: "uppercase",
                }}
                dy={10}
                interval={0}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fontWeight: 600, fill: "#A1A1AA" }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                cursor={{ fill: "#EFF6FF" }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    return (
                      <div className="bg-[#1D1D1F] text-white p-3 rounded-lg shadow-2xl border border-white/10 min-w-[160px]">
                        <p className="text-[10px] font-bold uppercase text-white/50 mb-1 tracking-wider">
                          {d.fullName}
                        </p>
                        <div className="flex items-baseline justify-between">
                          <span
                            className="text-2xl font-bold"
                            style={{ color: getBarColor(d.status) }}
                          >
                            {d.pct}%
                          </span>
                          <span className="text-xs font-bold text-white/80">
                            {fmtCompact(d.amt)}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar
                dataKey="pct"
                radius={[6, 6, 0, 0]}
                isAnimationActive={true}
                animationDuration={800}
              >
                {currentData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getBarColor(entry.status)}
                  />
                ))}
              </Bar>
              <ReferenceLine y={100} stroke="#E5E5EA" strokeDasharray="3 3" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </BentoCard>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
export default function AdminOverview() {
  const { data: sponsorships } = useSponsorships({ refetchInterval: 5000 });
  const { data: payments } = usePayments({}, { refetchInterval: 5000 });
  const { data: backendStats } = usePaymentStats({ refetchInterval: 5000 }); // Fetch backend stats
  const { data: chapters } = useChapters();
  const { rates, loading: ratesLoading } = useCurrencyRates();

  /* ── Chapter stats export ── */
  const [isExporting, setIsExporting] = useState(false);
  const { refetch: fetchAllStats, isFetching: isFetchingStats } =
    useAllChaptersStats();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await fetchAllStats();
      if (result.data) {
        exportAllChapterStats(result.data);
      }
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setIsExporting(false);
    }
  };

  /* ── DATA LOGIC ── */
  const stats = useMemo(() => {
    if (!sponsorships || !chapters || ratesLoading) return null;

    // Helper to convert currency
    const toINR = (amount, currency) => {
      if (!amount) return 0;
      const rate = rates[currency] || rates["INR"];
      return amount * rate;
    };

    // If backendStats is available, use it for summary metrics (faster/scalable)
    // Otherwise fallback to client-side calculation (legacy/offline support)
    let summaryMetrics = {};

    if (backendStats) {
      const sumStats = (list) =>
        list.reduce((acc, curr) => acc + toINR(curr.totalAmount, curr._id), 0);
      const countStats = (list) =>
        list.reduce((acc, curr) => acc + curr.count, 0);

      summaryMetrics = {
        totalCollectedINR: sumStats(backendStats.total),
        totalPayments: countStats(backendStats.total),
        todayAmountINR: sumStats(backendStats.today),
        todayCount: countStats(backendStats.today),
        yesterdayAmountINR: sumStats(backendStats.yesterday),
        yesterdayCount: countStats(backendStats.yesterday),
        thisMonthAmountINR: sumStats(backendStats.thisMonth),
        thisMonthCount: countStats(backendStats.thisMonth),
        // For trends, we might still need individual payments or a better backend structure
        // Using client-side fallbacks for complex charts if backend doesn't provide enough detail yet
      };
    }

    const allPayments = payments || [];
    // ... existing logic for fallbacks or granular data ...
    const now = new Date();
    // ... (rest of date logic) ...
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfDayBefore = new Date(startOfToday);
    startOfDayBefore.setDate(startOfDayBefore.getDate() - 2);
    const startOf7DaysAgo = new Date(startOfToday);
    startOf7DaysAgo.setDate(startOf7DaysAgo.getDate() - 7);
    const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const inRange = (d, from, to) => {
      const t = new Date(d).getTime();
      return t >= from.getTime() && t < to.getTime();
    };

    const paymentAmountINR = (list) =>
      list.reduce((s, p) => s + toINR(p.amount, p.currency), 0);

    // Calculate these anyway for charts/leaderboards that rely on 'allPayments'
    const todayPayments = allPayments.filter((p) =>
      inRange(p.createdAt, startOfToday, now),
    );
    const yesterdayPayments = allPayments.filter((p) =>
      inRange(p.createdAt, startOfYesterday, startOfToday),
    );
    const dayBeforePayments = allPayments.filter((p) =>
      inRange(p.createdAt, startOfDayBefore, startOfYesterday),
    );
    const last7Payments = allPayments.filter((p) =>
      inRange(p.createdAt, startOf7DaysAgo, now),
    );
    const thisMonthPayments = allPayments.filter((p) =>
      inRange(p.createdAt, startOfThisMonth, now),
    );

    // If backendStats wasn't ready or failed, use client-side calculation
    if (!summaryMetrics.totalCollectedINR && allPayments.length > 0) {
      summaryMetrics = {
        totalCollectedINR: allPayments.reduce(
          (s, p) => s + toINR(p.amount, p.currency),
          0,
        ),
        totalPayments: allPayments.length,
        todayAmountINR: paymentAmountINR(todayPayments),
        todayCount: todayPayments.length,
        yesterdayAmountINR: paymentAmountINR(yesterdayPayments),
        yesterdayCount: yesterdayPayments.length,
        yesterdayAmountINR: paymentAmountINR(yesterdayPayments), // Redundant but safe
        thisMonthAmountINR: paymentAmountINR(thisMonthPayments),
        thisMonthCount: thisMonthPayments.length,
      };
    }

    // Sponsorships logic (unchanged)
    const mahalluSponsors = sponsorships.filter((s) => s.type === "Mahallu");
    const individualSponsors = sponsorships.filter(
      (s) => s.type === "Individual",
    );

    const catStats = (list) => {
      let promisedINR = 0,
        paidINR = 0,
        active = 0,
        pending = 0;
      list.forEach((sp) => {
        promisedINR += toINR(sp.amountPromised, sp.currency);
        paidINR += toINR(sp.paidAmount, sp.currency);
        if (sp.status === "active") active++;
        if (sp.status === "pending") pending++;
      });
      return {
        count: list.length,
        promisedINR: Math.round(promisedINR),
        paidINR: Math.round(paidINR),
        conversionPct:
          promisedINR > 0 ? Math.round((paidINR / promisedINR) * 100) : 0,
        active,
        pending,
      };
    };

    const chMap = {};
    chapters.forEach((c) => {
      const costINR = c.targetConfig?.costPerStudentINR || 500;
      chMap[c._id] = {
        name: c.chapterName,
        targetStudents: c.targetCount || 0,
        targetINR: (c.targetCount || 0) * costINR,
        collectedINR: 0,
      };
    });

    allPayments.forEach((p) => {
      const chId = typeof p.chapter === "object" ? p.chapter?._id : p.chapter;
      if (chMap[chId]) chMap[chId].collectedINR += toINR(p.amount, p.currency);
    });

    const chapterLeaderboard = Object.values(chMap)
      .map((ch) => ({
        name: ch.name,
        targetINR: ch.targetINR,
        collectedINR: Math.round(ch.collectedINR),
        completionPct:
          ch.targetINR > 0
            ? Math.min(Math.round((ch.collectedINR / ch.targetINR) * 100), 100)
            : 0,
      }))
      .sort((a, b) => b.completionPct - a.completionPct);

    // Calculate Global Target & Top Day
    const totalTargetINR = chapterLeaderboard.reduce(
      (acc, curr) => acc + curr.targetINR,
      0,
    );
    const globalCompletionPct =
      totalTargetINR > 0
        ? Math.round((summaryMetrics.totalCollectedINR / totalTargetINR) * 100)
        : 0;

    let topDayStats = { date: "-", amount: 0 };

    if (backendStats && backendStats.dailyStats) {
      const dailyMap = {};
      backendStats.dailyStats.forEach((d) => {
        const date = d._id.date;
        dailyMap[date] =
          (dailyMap[date] || 0) + toINR(d.totalAmount, d._id.currency);
      });
      const sortedDays = Object.entries(dailyMap).sort((a, b) => b[1] - a[1]);
      if (sortedDays.length > 0) {
        topDayStats = {
          date: sortedDays[0][0],
          amount: sortedDays[0][1],
        };
      }
    } else if (allPayments.length > 0) {
      // Fallback
      const dailyMap = {};
      allPayments.forEach((p) => {
        const d = new Date(p.createdAt).toISOString().split("T")[0];
        dailyMap[d] = (dailyMap[d] || 0) + toINR(p.amount, p.currency);
      });
      const sortedDays = Object.entries(dailyMap).sort((a, b) => b[1] - a[1]);
      if (sortedDays.length > 0) {
        topDayStats = {
          date: sortedDays[0][0],
          amount: sortedDays[0][1],
        };
      }
    }

    // Prepare chart data (Last 7 Days)
    let chartData = [];
    // Initialize empty days for strict X-Axis
    const daysMap = {};
    for (let i = 6; i >= 0; i--) {
      const d = new Date(startOfToday);
      d.setDate(d.getDate() - i);
      const dayName = d.toLocaleDateString("en-US", { weekday: "short" }); // e.g., Mon
      daysMap[dayName] = 0;
    }

    if (backendStats && backendStats.last7Days) {
      backendStats.last7Days.forEach((day) => {
        const dayName = new Date(day._id.date).toLocaleDateString("en-US", {
          weekday: "short",
        });
        if (daysMap[dayName] !== undefined) {
          daysMap[dayName] += toINR(day.totalAmount, day._id.currency);
        }
      });
    } else {
      // Fallback or if using allPayments
      last7Payments.forEach((p) => {
        const dayName = new Date(p.createdAt).toLocaleDateString("en-US", {
          weekday: "short",
        });
        if (daysMap[dayName] !== undefined) {
          daysMap[dayName] += toINR(p.amount, p.currency);
        }
      });
    }

    chartData = Object.keys(daysMap).map((name) => ({
      name,
      val: daysMap[name],
    }));

    return {
      ...summaryMetrics, // Mix in the backend (or fallback) summary stats
      last7Days: chartData,
      dayBeforeYesterdayAmountINR: paymentAmountINR(dayBeforePayments), // Needed for comparisons
      last7DaysAmountINR: paymentAmountINR(last7Payments),
      last7DaysCount: last7Payments.length,
      prev7DaysAmountINR: 0,
      totalSponsorships: sponsorships.length,
      sponsorshipCategories: {
        mahallu: catStats(mahalluSponsors),
        individual: catStats(individualSponsors),
      },
      chapterLeaderboard,
      totalTargetINR,
      globalCompletionPct,
      topDayStats,
    };
  }, [sponsorships, payments, backendStats, chapters, rates, ratesLoading]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const slides = [
    {
      id: "payments",
      label: "Overview",
      component: <PaymentsSlide stats={stats} />,
    },
    {
      id: "sponsorships",
      label: "Sponsors",
      component: <SponsorshipsSlide stats={stats} />,
    },
    {
      id: "chapters",
      label: "Rankings",
      component: <ChapterLeaderboardSlide stats={stats} />,
    },
  ];

  useEffect(() => {
    if (!isPlaying || !stats) return;
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setCurrentSlide((c) => (c + 1) % slides.length);
          return 0;
        }
        return p + (100 / SLIDE_DURATION) * 100;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [isPlaying, stats]);

  if (!stats)
    return (
      <div className="h-[700px] w-full bg-[#F5F5F7] flex items-center justify-center text-[#0071E3]/60 font-mono tracking-widest uppercase animate-pulse">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        Syncing FX Rates...
      </div>
    );

  return (
    <div
      className={cn(
        "flex flex-col bg-[#F5F5F7] text-[#1D1D1F] font-sans transition-all duration-300",
        isFullscreen
          ? "fixed inset-0 z-50 h-screen w-screen"
          : "h-[calc(100vh-4rem)] min-h-[700px] rounded-[24px] overflow-hidden border border-[#E5E5EA] shadow-xl",
      )}
    >
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); filter: blur(4px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
      `}</style>
      <HatchPattern />
      <div className="flex items-center justify-between px-8 py-5 bg-[#F5F5F7]/80 backdrop-blur-sm border-b border-[#E5E5EA] z-20 sticky top-0">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold tracking-tight uppercase text-[#1D1D1F]">
            Taawun<span className="text-[#0071E3]">.</span>OS
          </h1>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex gap-1 bg-[#E5E5EA]/50 p-1 rounded-full">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => {
                  setCurrentSlide(i);
                  setProgress(0);
                }}
                className={cn(
                  "px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all",
                  currentSlide === i
                    ? "bg-white shadow-sm text-[#1D1D1F]"
                    : "text-[#86868B] hover:text-black",
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="h-6 w-[1px] bg-[#E5E5EA]" />
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsPlaying(!isPlaying)}
              className="hover:bg-white rounded-full"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 text-[#86868B]" />
              ) : (
                <Play className="h-5 w-5 text-[#86868B]" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className={cn(
                "hover:bg-white rounded-full",
                isFullscreen && "bg-[#0071E3] hover:bg-[#0071E3] text-white",
              )}
            >
              {isFullscreen ? (
                <Minimize2 className="h-5 w-5" />
              ) : (
                <Maximize2 className="h-5 w-5 text-[#86868B]" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleExport}
              disabled={isExporting || isFetchingStats}
              title="Export all chapter stats to Excel"
              className="hover:bg-white rounded-full"
            >
              {isExporting || isFetchingStats ? (
                <RefreshCw className="h-5 w-5 text-[#0071E3] animate-spin" />
              ) : (
                <FileDown className="h-5 w-5 text-[#86868B]" />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex-1 relative overflow-hidden bg-[#F5F5F7]">
        <div
          key={currentSlide}
          className="absolute inset-0 animate-[fadeUp_0.8s_cubic-bezier(0.16,1,0.3,1)_forwards]"
        >
          {slides[currentSlide].component}
        </div>
      </div>
      <div className="h-1.5 w-full bg-[#E5E5EA]">
        <div
          className="h-full bg-[#0071E3] transition-all duration-100 ease-linear shadow-[0_0_10px_#0071E3]"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
